import React from "react";
import { Input } from "@campgladiator/cgui-core.atoms.input";
import { ListItemProps } from "@campgladiator/cgui-core.atoms.list-item";
import { ListMenu } from "@campgladiator/cgui-core.molecules.list-menu";
import { useDropdown } from ".";
import styles from "./dropdown.module.scss";

export type Option = ListItemProps & {
  type: "item";
  isDefault?: boolean;
  metadata?: { [key: string]: any };
};

export type DropdownProps = {
  /** Disables the input field, preventing its use. */
  disabled?: boolean;
  /** Callback prop which returns the selectedOption object once an option has been selected by the user in the dropdown. */
  onChangeSelectedOption?: (option?: Option) => void;
  /** The item/s you want to have displayed in the dropdown. Must follow the type Option pattern. You can also pass in an optional metadata property which will accept any type of data in an object. This is useful for passing custom data about the selected option to the controller. */
  options?: Option[];
} & React.InputHTMLAttributes<HTMLInputElement>;

const Dropdown = ({
  disabled = false,
  options = [{ itemId: "0", type: "item", text: "Select An Option" }],
  onChangeSelectedOption = () => {},
  ...props
}: DropdownProps) => {
  const {
    contentRef,
    formattedOptionsForItemList,
    handleFocus,
    icon,
    inputRef,
    isActive,
    listMenuRef,
    onIconClick,
    selectedOption,
    setIsActive,
  } = useDropdown({
    onChangeSelectedOption,
    options,
    placeHolder: props.placeholder,
  });

  return (
    <div className={styles.dropdown} ref={contentRef}>
      <Input
        {...props}
        name={props.name}
        id={props.id}
        value={selectedOption.text}
        readOnly
        className={styles.input}
        ref={inputRef}
        contentEditable={false}
        onFocus={() => setIsActive(true)}
        onClick={handleFocus}
        disabled={disabled}
        onIconClick={onIconClick}
        icon={icon}
      />
      {isActive && (
        <ListMenu
          ref={listMenuRef}
          selection="single"
          initialActiveIds={new Set(selectedOption.itemId)}
          className={styles.menu}
          items={formattedOptionsForItemList(options)}
          size="small"
        />
      )}
    </div>
  );
};

export default Dropdown;
